import { NOTE_SECTION } from '@/constants/note-section';
import { GET_NOTE_CONTENT } from '@ax/graphql';
import { convertUTCtoLocalTime, formatTimeToShortAMPM } from '@/utils';
import { useQuery } from '@apollo/client';
import { NoteItem, NoteSectionSentence } from '@ax/service/src/ehr-interface';
import { noteContentLoaded, useAppDispatch } from '@ax/redux-store';
import { useEffect } from 'react';
import { NOTE_STATUS } from '@ax/constants';

export const useGetNoteParagraphsAndSentences = ({
  noteId,
  noteStatus,
  style,
}: {
  noteId: string | number;
  noteStatus: NOTE_STATUS;
  style: CSSModuleClasses;
}) => {
  const dispatch = useAppDispatch();

  const {
    data: noteContentData,
    loading,
    error,
  } = useQuery(GET_NOTE_CONTENT, {
    variables: { noteId, contentOptions: { noteId } },
    fetchPolicy: 'network-only',
    pollInterval: 3000,
    skip: !noteId,
  });

  useEffect(() => {
    if (!loading && !error && noteContentData && noteStatus !== NOTE_STATUS.CONTENT_PROCESSING) {
      dispatch(noteContentLoaded({ noteContent: noteContentData, noteId: noteId.toString() }));
    }
  }, [loading, error, noteContentData, dispatch, noteStatus, noteId]);

  const aiNoteInfo = { isAIGeneratedNote: false, aINoteTime: '', generatedAt: '' };

  const hpiSectionSentences = filterNoteBySection(noteContentData, NOTE_SECTION.hpi, aiNoteInfo);
  const intervalHistorySectionSentences = filterNoteBySection(
    noteContentData,
    NOTE_SECTION.intervalHistory,
    aiNoteInfo,
  );
  const rosSectionSentences = filterNoteBySection(noteContentData, NOTE_SECTION.ros, aiNoteInfo);
  const peSectionSentences = filterNoteBySection(noteContentData, NOTE_SECTION.pe, aiNoteInfo);
  const apSectionSentences = filterNoteBySection(noteContentData, NOTE_SECTION.ap, aiNoteInfo);
  const mdmSectionSentences = filterNoteBySection(noteContentData, NOTE_SECTION.mdm, aiNoteInfo);
  const progressSectionSentences = filterNoteBySection(
    noteContentData,
    NOTE_SECTION.progress,
    aiNoteInfo,
  );
  const wholeNoteSectionSentences = filterNoteBySection(
    noteContentData,
    NOTE_SECTION.whole_note,
    aiNoteInfo,
  );

  const hpiSectionParagraphs = generateParagraphs(hpiSectionSentences);
  const intervalHistoryParagraphs = generateParagraphs(intervalHistorySectionSentences);
  const apSectionParagraphs = generateParagraphs(apSectionSentences);
  const rosSentences = listSentences(rosSectionSentences, style);
  const wholeNoteSentences = listSentences(wholeNoteSectionSentences, style);
  const peSentences = listSentences(peSectionSentences, style);
  const mdmSectionParagraphs = listSentences(mdmSectionSentences, style);
  const progressSentences = listProgressSentences(progressSectionSentences);

  return {
    hpiSectionSentences,
    intervalHistorySectionSentences,
    rosSectionSentences,
    peSectionSentences,
    apSectionSentences,
    mdmSectionSentences,
    progressSectionSentences,
    hpiSectionParagraphs,
    intervalHistoryParagraphs,
    wholeNoteSectionSentences,
    wholeNoteSentences,
    rosSentences,
    peSentences,
    apSectionParagraphs,
    mdmSectionParagraphs,
    progressSentences,
    loading,
    isAIGeneratedNote: aiNoteInfo.isAIGeneratedNote,
    aINoteTime: aiNoteInfo.aINoteTime,
    hasNoteContent: !!noteContentData?.getNoteContent?.length,
    generatedAt: aiNoteInfo.generatedAt,
  };
};

export const filterNoteBySection = (
  noteContentData: any,
  section: string,
  aiNoteInfo: { isAIGeneratedNote: boolean; aINoteTime: string; generatedAt: string },
) => {
  const noteContent = noteContentData?.getNoteContent?.filter(
    (item: NoteItem) => item.Section === section,
  );
  if (noteContent) {
    const groupedNoteData: { [key: string]: NoteItem[] } = {};

    noteContent.forEach((item: NoteItem) => {
      if (item.templateSelection) {
        const { ComplaintId, ComplaintName } = item.templateSelection;
        const key = `${ComplaintId}-${ComplaintName}`;

        if (!groupedNoteData[key]) {
          groupedNoteData[key] = [];
        }

        groupedNoteData[key].push(item);
      } else {
        const key = 'null-templateSelection';

        if (!groupedNoteData[key]) {
          groupedNoteData[key] = [];
        }

        groupedNoteData[key].push(item);
      }
    });

    const transformedNoteData = Object.entries(groupedNoteData).map(([_, contentArray]) => {
      const { templateSelection, Section, UpdatedAt } = contentArray[0];
      const { ComplaintId, ComplaintName } = templateSelection || {};
      const sentences = contentArray.map(
        ({ Content, Order, CreatedAt, selection, Section, UpdatedAt, ActionSource }) => ({
          Sentence: Content,
          Order,
          CreatedAt,
          selection,
          Section,
          UpdatedAt,
          ActionSource,
        }),
      );

      if (!aiNoteInfo.isAIGeneratedNote) {
        sentences.forEach((item) => {
          if (item.ActionSource === 'ML') {
            aiNoteInfo.isAIGeneratedNote = true;
            aiNoteInfo.aINoteTime = formatTimeToShortAMPM(convertUTCtoLocalTime(item.UpdatedAt));
            aiNoteInfo.generatedAt = item.UpdatedAt;
          }
        });
      }

      return {
        ComplaintId: ComplaintId || 0,
        ComplaintName: ComplaintName || '',
        Section,
        sentences,
        UpdatedAt,
      };
    });

    return transformedNoteData;
  } else {
    return [];
  }
};

const generateParagraphs = (sentences: NoteSectionSentence[]): JSX.Element[] => {
  return sentences.map((item, index) => (
    <div key={index}>
      <strong>{item.ComplaintName}</strong>
      <p>{item.sentences.map((sentence) => sentence.Sentence).join(' ')}</p>
    </div>
  ));
};

const listSentences = (
  sentences: NoteSectionSentence[],
  style: CSSModuleClasses,
): JSX.Element[] => {
  return sentences.map((item, index) => (
    <div key={index}>
      {item.ComplaintName && (
        <strong className={style.note__content__title}>{item.ComplaintName}</strong>
      )}
      {item.sentences.map((sentence, sentenceIndex) => (
        <p
          key={sentenceIndex}
          className={style.note__content__text}
        >
          {sentence.Sentence}
        </p>
      ))}
    </div>
  ));
};

const listProgressSentences = (sentences: NoteSectionSentence[]): JSX.Element[] => {
  return sentences.map((item, index) => (
    <div key={index}>
      {item.sentences.map((sentence, sentenceIndex) => (
        <div key={sentenceIndex}>
          <strong>Progress #{sentence.Order}</strong>
          <br />
          <strong>Time {formatTimeToShortAMPM(convertUTCtoLocalTime(sentence.CreatedAt))}</strong>
          <p>{sentence.Sentence}</p>
        </div>
      ))}
    </div>
  ));
};
